import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Flex,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { AutomationRunsList } from '@/modules/automation-details';

interface IRunHistoryDialogProps {
  automationId: string;
  /** Whether this is a draft automation (true) or published automation (false) */
  isDraft: boolean;
  /** Whether the dialog is open (controlled from parent) */
  isOpen: boolean;
  /** Callback when the open state changes */
  onOpenChange: (open: boolean) => void;
  /** Whether the trigger button is disabled */
  disabled?: boolean;
  /** Renders the icon-only trigger button. Set false when the dialog is
   *  opened from somewhere else — e.g. a page header's overflow menu — and
   *  only the controlled `isOpen`/`onOpenChange` pair drives it. */
  showTrigger?: boolean;
  /**
   * Height of the icon trigger. The draft rail's action row is 32px, so the
   * default 36px `icon` overflows it top and bottom; page headers sit beside
   * other 36px icon buttons and keep the default.
   */
  triggerSize?: 'icon' | 'icon-sm';
  /** Forwarded to the runs table empty-state CTA to start a run */
  onRun?: () => void;
  isRunLoading?: boolean;
}

/**
 * A dialog displaying run history for an automation.
 *
 * Reuses the automation dashboard runs table (`AutomationRunsList`) so the run
 * history follows the same behavior, columns and pagination as the dashboard,
 * but with the dialog filter layout (search + Status + Date range) and a
 * 6-row page size per design. The table self-fetches its data; Radix unmounts
 * the dialog content when closed, so runs are fetched only while it is open.
 *
 * @param props - Component props
 * @returns JSX element for the run history dialog
 */
export function RunHistoryDialog({
  automationId,
  isDraft,
  isOpen,
  onOpenChange,
  disabled = false,
  showTrigger = true,
  triggerSize = 'icon',
  onRun,
  isRunLoading = false,
}: IRunHistoryDialogProps) {
  const { t: tAutomation } = useTranslation('automation');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {showTrigger && (
        <Tooltip delayDuration={200} variant="primary">
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                size={triggerSize}
                variant="outline"
                aria-label={tAutomation('automation.runs.history')}
                disabled={disabled}
              >
                <Icon type="ListVideo" size="sm" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {tAutomation('automation.header.tooltip.runHistory')}
          </TooltipContent>
        </Tooltip>
      )}
      <DialogContent
        // Fixed height (capped on short viewports) so the dialog doesn't
        // resize as the row count changes between pages — only the inner list
        // area scrolls. Sized to comfortably fit a full 6-row page.
        // bg-popover matches the run-history design surface (#262626) instead
        // of the default dialog bg-background, so the table blends with the
        // modal rather than sitting on a darker inset panel.
        className="flex flex-col w-[1264px] sm:max-w-[90vw] h-[732px] max-h-[92vh] p-4 bg-popover"
        showCloseButton
      >
        <DialogHeader>
          {/* pl-1.5 matches the AutomationRunsList root's px-1.5 inset so the
              title's left edge lines up with the table below it. */}
          <DialogTitle className="font-normal pl-1.5 text-2xl leading-8">
            {tAutomation('automation.runs.history')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {tAutomation('automation.runs.historyDescription')}
          </DialogDescription>
        </DialogHeader>
        <Flex direction="column" className="flex-1 min-h-0 overflow-y-auto">
          <AutomationRunsList
            automationId={automationId}
            isDraft={isDraft}
            onRun={onRun}
            onViewRun={() => onOpenChange(false)}
            isRunLoading={isRunLoading}
            showTitle={false}
            showSearch
            pageSize={6}
            persistFiltersInUrl={false}
          />
        </Flex>
      </DialogContent>
    </Dialog>
  );
}
